import {observer} from "mobx-react";
import * as React from "react";
import {withAppStores} from "../../../../AppStores";
import {View} from "../../../../data/models/View";
import type {IViewSort, ViewChangeType} from "../../../../data/models/ViewUtils";
import {notify} from "../../../../utils/Notify";
import {ObjectUtils} from "../../../../utils/data/ObjectUtils";
import {NotificationType} from "../../../notification/Notification";
import {Permission, XyiconFeature} from "../../../../generated/api/base";
import type {TransportLayer} from "../../../../data/TransportLayer";
import type {AppState} from "../../../../data/state/AppState";
import {PromptWindowV5} from "../../popup/PromptWindowV5";
import {GlobalSharingPopup} from "../../sharing/GlobalSharingPopup";
import {ButtonV5} from "../../button/ButtonV5";
import {onWorkspaceViewClick} from "../../topbar/ViewTabsCommon";

interface ISaveToViewButtonProps {
	readonly appState?: AppState;
	readonly transport?: TransportLayer;
	readonly feature: XyiconFeature;
	readonly viewChangeType: ViewChangeType[];
	readonly onSaveClicked?: () => void;
	readonly disabled?: boolean;
	readonly isSideGrid?: boolean;
	readonly isOpen?: boolean;
	readonly hasUnsavedChanges?: boolean;
}

interface ISaveToViewButtonState {
	isLoading: boolean;
	isGlobalActionPanelOpen: boolean;
}

@withAppStores("appState", "transport")
@observer
export class SaveToViewButtonV5 extends React.Component<ISaveToViewButtonProps, ISaveToViewButtonState> {
	private _isMounted: boolean = false;

	constructor(props: ISaveToViewButtonProps) {
		super(props);
		this.state = {
			isLoading: false,
			isGlobalActionPanelOpen: false,
		};
	}

	private onSaveAsNewViewClick = async () => {
		const view = this.selectedView;
		const config = {
			ok: "Save As",
			cancel: "",
		};
		const name = await PromptWindowV5.open("", `Save View As`, `${view.name} - Copy`, `${view.name} - Copy`, config);

		if (name) {
			await this.saveNewView(name);
			this.props.onSaveClicked?.();
		}
	};

	public onSaveToViewClick = async () => {
		const view = this.selectedView;

		if (view.isGlobal) {
			this.setState({
				isGlobalActionPanelOpen: true,
			});
		} else {
			await this.saveView();
			this.props.onSaveClicked?.();
		}
	};

	private get selectedView() {
		return this.props.appState.actions.getSelectedView(this.props.isSideGrid ? XyiconFeature.SpaceEditor : this.props.feature);
	}

	private saveNewDataToView(viewToSave: View) {
		for (const type of this.props.viewChangeType) {
			const newData = this.selectedView.getNewDataForType(type) as any;

			switch (type) {
				case "captions":
					viewToSave.spaceEditorViewSettings.captions = newData;
					break;
				case "columns":
					viewToSave.setSerializedColumns(JSON.stringify(this.selectedView.getNewDataForType("columns")));
					viewToSave.setSorts(this.selectedView.getNewDataForType("column sorts") as IViewSort[]);
					break;
				case "spotlight": {
					viewToSave.spotlightRules = newData;
					break;
				}
				case "filters":
					viewToSave.setFilters(newData);
					break;
				case "layers":
					viewToSave.spaceEditorViewSettings.layers = newData;
					break;
			}
		}
	}

	private resetDataForSelectedView(viewToResetTo: View) {
		const selectedView = this.selectedView;

		selectedView.setFilters(ObjectUtils.deepClone(viewToResetTo.filters));
		selectedView.setSerializedColumns(viewToResetTo.getSerializedColumns());
		selectedView.setSorts(ObjectUtils.deepClone(viewToResetTo.sorts));

		const resetAdditionalDetails = JSON.parse(viewToResetTo.data.additionalDetails || "{}");
		selectedView.spotlightRules = resetAdditionalDetails.spotlightRules || {
			xyiconSpotlightRules: [],
			boundarySpotlightRules: [],
			listViewSpotlightRules: [],
		};

		if (selectedView.spaceEditorViewSettings) {
			selectedView.spaceEditorViewSettings.captions = viewToResetTo.spaceEditorViewSettings.captions;
			selectedView.spaceEditorViewSettings.formattingRules = viewToResetTo.spaceEditorViewSettings.formattingRules;
			selectedView.spaceEditorViewSettings.layers = viewToResetTo.spaceEditorViewSettings.layers;
		}
	}

	private async saveNewView(newViewName: string) {
		this.setState({
			isLoading: true,
		});

		const {appState, transport} = this.props;
		const selectedView = this.selectedView;

		const currentData = selectedView.getSavedData(); //get all of selectedView's data
		const savedView = new View(currentData, appState);
		const feature = this.props.isSideGrid ? XyiconFeature.SpaceEditor : this.props.feature;

		// Save only the data that is associated with the viewDataType,
		// But after creating the new view, reset the state of selected view before selecting the newly created view
		this.saveNewDataToView(savedView);

		const dataToSave = ObjectUtils.deepClone(savedView.getData());
		dataToSave.viewSharingSettings.length = 0;

		dataToSave.name = newViewName;
		dataToSave.ownerUserID = appState.user?.id || "";
		dataToSave.isGlobal = false; // for both admins + non-admins, "save as" views are created as private views. User must promote to global afterwards if needed.

		const {result: viewData, error} = await transport.services.view.create(dataToSave, feature);

		if (viewData?.viewID) {
			// Reset the data of the selected view
			const savedView = new View(currentData, appState);

			this.resetDataForSelectedView(savedView);

			// Select the newly created view
			appState.actions.selectViewById(viewData.viewID);
			onWorkspaceViewClick(this.selectedView);
		} else if (error) {
			notify(this.props.appState.app.notificationContainer, {
				type: NotificationType.Error,
				title: "Error",
				lifeTime: Infinity,
				description: error.ErrorMessage || "Unknown error",
			});
		}

		if (this._isMounted) {
			this.setState({
				isLoading: false,
			});
		}
	}

	private saveView = async () => {
		this.setState({
			isLoading: true,
		});

		const {appState, transport} = this.props;
		const selectedView = this.selectedView;
		const savedData = selectedView.getSavedData();
		const savedView = new View(savedData, appState);

		// Save only the data that is associated with the viewDataType,
		// But after the update-on-the-backend, update the view with the rest of the (unsaved) modifications
		this.saveNewDataToView(savedView);
		this.saveNewDataToView(selectedView);

		const dataToSave = savedView.getData();

		await transport.services.view.update(dataToSave);

		//We are not saving the unsaved changes to the current view.. we are resetting them..
		const newUpdatedData = selectedView.getSavedData();

		// Update the current view with out the unsaved modifications
		const currentView = new View(newUpdatedData, appState);

		this.resetDataForSelectedView(currentView);

		if (this._isMounted) {
			this.setState({
				isLoading: false,
			});
		}
	};

	public override componentDidMount() {
		this._isMounted = true;
	}

	public override componentWillUnmount() {
		this._isMounted = false;
	}

	public override render() {
		const view = this.selectedView;
		const {hasUnsavedChanges} = this.props;
		const permissionAllowed = this.selectedView.getPermission(this.props.appState.user.id) >= Permission.Update;

		// in case of manageColumns we don't want to detect column width change in that case
		if (hasUnsavedChanges) {
			return (
				<>
					{this.state.isGlobalActionPanelOpen && (
						<GlobalSharingPopup
							viewItems={[view]}
							onClose={() => this.setState({isGlobalActionPanelOpen: false})}
							viewActionType="modify"
							isReports={false}
							onConfirm={this.saveView}
						/>
					)}
					{this.props.isOpen && (
						<div className="right-buttons">
							<ButtonV5
								label="Save As"
								type="secondary"
								disabled={this.state.isLoading || this.props.disabled}
								onClick={this.onSaveAsNewViewClick}
							/>
							{permissionAllowed && (
								<ButtonV5
									label="Save"
									disabled={this.state.isLoading || this.props.disabled}
									onClick={this.onSaveToViewClick}
								/>
							)}
						</div>
					)}
					{!this.props.isOpen &&
						(permissionAllowed ? <p onClick={this.onSaveToViewClick}>Save View</p> : <p onClick={this.onSaveAsNewViewClick}>Save View As</p>)}
				</>
			);
		}

		return null;
	}
}
